import { useMemo } from 'react'
import type { ComponentProps } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import computeSponsoredOfferPlacements from 'lib/offer/computeSponsoredOfferPlacements'
import { EmptyArray } from 'lib/array/arrayUtils'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import type { OfferListV2Item } from 'components/OfferList/OfferList'

export interface Options extends Pick<ComponentProps<typeof OfferListOfferTile>, 'showPerNightPricing' | 'searchTileDensity' | 'preventNavigationToOfferPage' | 'storeOfferListTile'> {
  pageStart?: number
  pageSize?: number
}

export interface SponsoredOfferPlacements {
  items: Array<OfferListV2Item>
  excludeOfferIds: Array<string>
}

const EMPTY_RESULT: SponsoredOfferPlacements = { items: [], excludeOfferIds: [] }

function useSponsoredOfferPlacements(
  filters: App.OfferListFilters | undefined,
  offerIds: Array<string>,
  options: Options = {},
): SponsoredOfferPlacements {
  const { showPerNightPricing, searchTileDensity, preventNavigationToOfferPage, storeOfferListTile, pageStart = 0, pageSize } = options

  const listKey = useMemo(() => filters ? getOfferListKey(filters) : '', [filters])
  const sponsoredOffers = useAppSelector(state => state.offer.offerLists[listKey]?.sponsoredOffers ?? EmptyArray)
  const viewPriceInPoints = !!filters?.viewPriceInPoints

  return useMemo(() => {
    if (!sponsoredOffers.length) return EMPTY_RESULT

    const promoted = computeSponsoredOfferPlacements(sponsoredOffers, offerIds)
    const excludeOfferIds = promoted.map(placement => placement.offerId)

    const placements = promoted
      .filter(placement => offerIds.length >= placement.rawOffset)
      .map(placement => ({ ...placement, renderPosition: placement.rawOffset - pageStart }))

    const pageFiltered = pageSize === undefined
      ? placements
      : placements.filter(placement => placement.renderPosition >= 0 && placement.renderPosition < pageSize)

    const items = pageFiltered.map((placement): OfferListV2Item => ({
      id: `sponsored-${placement.offerId}`,
      position: placement.renderPosition,
      element: <OfferListOfferTile
        offerId={placement.offerId}
        position={placement.renderPosition}
        filters={filters}
        viewPriceInPoints={viewPriceInPoints}
        tileStyle="search"
        wrapperClass="offer-tile"
        showPerNightPricing={showPerNightPricing}
        searchTileDensity={searchTileDensity}
        preventNavigationToOfferPage={preventNavigationToOfferPage}
        storeOfferListTile={storeOfferListTile}
      />,
    }))

    return { items, excludeOfferIds }
  }, [
    sponsoredOffers,
    offerIds,
    pageStart,
    pageSize,
    filters,
    viewPriceInPoints,
    showPerNightPricing,
    searchTileDensity,
    preventNavigationToOfferPage,
    storeOfferListTile,
  ])
}

export default useSponsoredOfferPlacements
